import { API } from '../configs/axios/axios.config'
import getConfig from '../configs/config'
import fileDownload from 'js-file-download'
import { VERSION_ID_PARAM } from '../constants/query-params'
import { LLM_VERSIONS } from '../constants/llm-versions'
import { isNil } from 'lodash'

const GET_ASSIGNMENT_LAYERS_ENDPOINT = token => `/assignment/${token}/get-assignment-layers`
const GET_ASSIGNMENT_VERSIONS_ENDPOINT = token => `/versions/${token}`
const GET_POSSIBLE_TO_COPY_ENDPOINT = token => `/versions/${token}/possible-to-copy`
const GET_ASSIGNMENT_ENDPOINT = token => `/assignment/${token}/assignment`
const GET_FILES_ENDPOINT = token => `/assignment/${token}/get-files`
const GET_LAYER_PATTERNS_ENDPOINT = token => `/patterns/${token}`
const GET_PATTERNS_REPORT_ENDPOINT = token => `/patterns/${token}/report`
const GET_LAYER_CLUSTERS_ENDPOINT = token => `/clusters/${token}/get-layer-clusters`
const CREATE_LAYER_ENDPOINT = token => `/assignment/${token}/create-layer`
const UPDATE_VERSION_ENDPOINT = token => `/versions/${token}`
const GET_QUERY_MATCHES_ENDPOINT = token => `/query/${token}/get-query-matches`
const GET_QUERY_ENDPOINT = token => `/query/${token}/get-query`
const COMPARE_QUERIES_ENDPOINT = token => `/query/${token}/compare-queries`
const UPDATE_PATTERN_ENDPOINT = (token, layerId, patternId) => `/patterns/${token}/${layerId}/${patternId}`
const ARCHETYPE_DISCOVERY_ENDPOINT = (token, layerId) => `/learn-by-example/${token}/archetype-discovery/${layerId}`
const DOWNLOAD_FILE_ENDPOINT = (token, fileId) => `/assignment/${token}/get-file-data/${fileId}`
const DOWNLOAD_SUBMISSIONS_ENDPOINT = token => `/assignment/${token}/download-files-as-zip`
const DELETE_PATTERN_ENDPOINT = (token, layerId, patternId) => `/patterns/${token}/${layerId}/${patternId}`
const UPDATE_CLUSTER_ENDPOINT = (token, layerId, clusterId) => `/clusters/${token}/${layerId}/${clusterId}`
const UPDATE_COMPONENT_ENDPOINT = token => `/assignment/${token}/update-layer`
const UPDATE_GRADING_ENDPOINT = token => `/assignment/${token}/update-grading`
const UPDATE_FILE_GRADE_ENDPOINT = (token, layerId, fileId) => `/assignment/${token}/${layerId}/${fileId}`
const GET_GROUPS_UNIT_TESTS_REPORT_ENDPOINT = token => `/unit-tests/${token}/groups-ut-report`
const DELETE_LAYER_ENDPOINT = (token, layerId) => `/assignment/${token}/delete-layer/${layerId}`
const RESET_LAYER_ENDPOINT = (token, layerId) => `/assignment/${token}/reset-layer/${layerId}`
const COPY_LAYER_ENDPOINT = (token, layerId) => `/assignment/${token}/copy-layer/${layerId}`
const IMPORT_CONFIG_ENDPOINT = token => `/assignment-tests/${token}/import-config`
const UPLOAD_TEST_FIXTURE_FILES_ENDPOINT = token => `/assignment-tests/${token}/upload-file`
const UPLOAD_REFERENCE_SOLUTION_ENDPOINT = token => `/assignment-metadata/${token}/upload-reference-solution`
const GET_REFERENCE_SOLUTION_ENDPOINT = token => `/assignment-metadata/${token}/get-reference-solution`
const GET_UNIT_TEST_RESULTS_ENDPOINT = token => `/unit-tests/${token}/ut-results`
const SEMANTIC_DISCOVERY_ENDPOINT = (token, layerId) => `/nl-feedback/${token}/discovery/${layerId}`
const GENERATE_AND_PROJECT_CRITERIA_ENDPOINT = (token, layerId) =>
    `/nl-feedback/generate-and-project-criteria/${token}/${layerId}`
const GENERATE_CRITERIA_ENDPOINT = token => `/nl-feedback/generate-criteria/${token}`
const PROJECT_CRITERIA_ENDPOINT = token => `/nl-feedback/project-criteria/${token}`
const CREATE_SEMANTIC_PATTERN_ENDPOINT = token => `/nl-feedback/${token}`
const UPDATE_PATTERN_FROM_FEEDBACK_ENDPOINT = token => `/nl-feedback/${token}`
const CREATE_SEMANTIC_PATTERNS_ENDPOINT = (token, layerId) => `/nl-feedback/${token}/batch/${layerId}`
const ESTIMATE_PROJECTION_COST_ENDPOINT = token => `/nl-feedback/estimate-projection-cost/${token}`
const ESTIMATE_DISCOVERY_COST_ENDPOINT = token => `/nl-feedback/estimate-discovery-cost/${token}`
const REGROUP_ENDPOINT = (token, layerId) => `/grouping/${token}/${layerId}/regroup`
const SUBGROUP_ENDPOINT = (token, layerId, submissionId) => `/grouping/${token}/${layerId}/subgroup/${submissionId}`
const GET_FILE_REASONING_ENDPOINT = (token, layerId, fileId, patternId) =>
    `/nl-feedback/${token}/${layerId}/reasoning/${fileId}/${patternId}`
const UPDATE_ASSIGNMENT_FILE_ENDPOINT = (token, layerId, fileId) => `/assignment-file/${token}/${layerId}/${fileId}`
const PATCH_ASSIGNMENT_ENDPOINT = token => `/assignment/${token}`
const GET_LINTER_CONFIG_ENDPOINT = token => `/assignment/${token}/linter-config`
const PATCH_LINTER_CONFIG_ENDPOINT = token => `/assignment/${token}/linter-config`

class _AssignmentService {
    getAssignment(token) {
        return API.get(GET_ASSIGNMENT_ENDPOINT(token))
    }

    patchAssignment(token, data) {
        return API.patch(PATCH_ASSIGNMENT_ENDPOINT(token), data)
    }

    getLinterConfig(token) {
        return API.get(GET_LINTER_CONFIG_ENDPOINT(token))
    }

    patchLinterConfig(token, data) {
        return API.patch(PATCH_LINTER_CONFIG_ENDPOINT(token), data)
    }

    getFiles(token, layerId, includeAnon) {
        return API.get(GET_FILES_ENDPOINT(token), {
            params: {
                layer_id: layerId,
                include_anon: includeAnon,
            },
        })
    }

    getFileReasoning(token, layerId, fileId, patternId) {
        return API.get(GET_FILE_REASONING_ENDPOINT(token, layerId, fileId, patternId))
    }

    updateAssignmentFile(token, layerId, fileId, isArchetype) {
        return API.put(UPDATE_ASSIGNMENT_FILE_ENDPOINT(token, layerId, fileId), {
            is_archetype: isArchetype,
        })
    }

    getLayerPatterns(token, layerId) {
        return API.get(GET_LAYER_PATTERNS_ENDPOINT(token), {
            params: {
                layer_id: layerId,
            },
        })
    }

    getPatternsReport(token, layerId) {
        return API.get(GET_PATTERNS_REPORT_ENDPOINT(token), {
            params: {
                layer_id: layerId,
            },
        })
    }

    getLayerClusters(token, layerId, includeAnon) {
        return API.get(GET_LAYER_CLUSTERS_ENDPOINT(token), {
            params: {
                layer_id: layerId,
                include_anon: includeAnon,
            },
        })
    }

    getGroupsUnitTestsReport(token) {
        return API.get(GET_GROUPS_UNIT_TESTS_REPORT_ENDPOINT(token))
    }

    getAssignmentLayers(token) {
        return API.get(GET_ASSIGNMENT_LAYERS_ENDPOINT(token))
    }

    getAssignmentVersions(token, publishedOnly) {
        return API.get(GET_ASSIGNMENT_VERSIONS_ENDPOINT(token), {
            params: {
                published_only: publishedOnly,
            },
        })
    }

    updateVersion({
        token,
        name,
        testsetPercentage,
        isDefault,
        isPublished,
        notifyChildren,
        changesHistory,
        ticketNumber,
        changesAuthor,
    }) {
        return API.put(UPDATE_VERSION_ENDPOINT(token), {
            name,
            testset_percentage: testsetPercentage,
            is_default: isDefault,
            is_published: isPublished,
            notify_children: notifyChildren,
            changes_history: changesHistory,
            ticket_number: ticketNumber,
            changes_author: changesAuthor,
        })
    }

    getPossibleToCopy(token) {
        return API.get(GET_POSSIBLE_TO_COPY_ENDPOINT(token))
    }

    createLayer(token, layerName) {
        return API.post(CREATE_LAYER_ENDPOINT(token), {
            name: layerName,
        })
    }

    updatePattern(
        token,
        layerId,
        id,
        name,
        feedback,
        description,
        grade,
        colorId,
        groupingCap,
        warningLevel,
        conceptualLabels,
        patternType,
        hitFeedback,
        missFeedback,
        showHitReasoning,
        showMissReasoning
    ) {
        return API.put(UPDATE_PATTERN_ENDPOINT(token, layerId, id), {
            motif_caption: name,
            feedback,
            description,
            grade,
            color_id: colorId,
            grouping_cap: groupingCap,
            warning_level: warningLevel,
            conceptual_labels: conceptualLabels,
            hit_feedback: hitFeedback,
            miss_feedback: missFeedback,
            show_hit_reasoning: showHitReasoning,
            show_miss_reasoning: showMissReasoning,
        })
    }

    getQueryMatches(token, layerId, queries, regroup, idOfPatternToChange) {
        return API.post(GET_QUERY_MATCHES_ENDPOINT(token), {
            layer_id: layerId,
            queries: queries,
            regroup: regroup,
            changed_pattern: idOfPatternToChange,
        })
    }

    getQuery(token, layerId, fileId, terms, regroup) {
        return API.post(GET_QUERY_ENDPOINT(token), {
            layer_id: layerId,
            file_id: fileId,
            terms: terms,
            regroup: regroup,
        })
    }

    compareQueries(token, layerId, patternId, query) {
        return API.post(COMPARE_QUERIES_ENDPOINT(token), {
            layer_id: layerId,
            pattern_id: patternId,
            query,
        })
    }

    downloadFile(token, fileId, downloadInNewTab = false, filename) {
        if (downloadInNewTab) {
            const versionId = new URLSearchParams(window.location.search).get(VERSION_ID_PARAM)
            let downloadFileUrl = DOWNLOAD_FILE_ENDPOINT(token, fileId)
            if (versionId) {
                downloadFileUrl = downloadFileUrl + `?${VERSION_ID_PARAM}=${versionId}`
            }
            window.open(getConfig().apiBaseUrl + downloadFileUrl, '_blank')
        } else {
            return API.get(DOWNLOAD_FILE_ENDPOINT(token, fileId), {
                responseType: 'blob',
            }).then(response => {
                fileDownload(response.data, `${filename}.xlsx`)
            })
        }
    }

    downloadSubmissions(token, modelName, includeHistory) {
        return API.get(DOWNLOAD_SUBMISSIONS_ENDPOINT(token), {
            responseType: 'blob',
            params: {
                history: includeHistory,
            },
        }).then(response => {
            const suffix = includeHistory ? '_all' : '_last'
            fileDownload(response.data, `${modelName}${suffix}_submissions.zip`)
        })
    }

    runArchetypeDiscovery(token, layerId) {
        return API.post(ARCHETYPE_DISCOVERY_ENDPOINT(token, layerId), {
            max_num_of_candidates: 20,
        })
    }

    deletePattern(token, layerId, patternId) {
        return API.delete(DELETE_PATTERN_ENDPOINT(token, layerId, patternId))
    }

    updateCluster(token, layerId, clusterId, name, feedback, grade, conceptualLabels) {
        return API.put(UPDATE_CLUSTER_ENDPOINT(token, layerId, clusterId), {
            cluster_name: name,
            feedback: feedback,
            grade: grade,
            conceptual_labels: conceptualLabels,
        })
    }

    updateComponent(token, layerId, layerName) {
        return API.put(UPDATE_COMPONENT_ENDPOINT(token), {
            layer_id: layerId,
            layer_name: layerName,
        })
    }

    updateGrading(token, layers) {
        return API.put(UPDATE_GRADING_ENDPOINT(token), {
            layers: layers,
        })
    }

    updateFileGrading(token, layerId, fileId, feedback, grade) {
        return API.put(UPDATE_FILE_GRADE_ENDPOINT(token, layerId, fileId), {
            feedback: feedback,
            grade: grade,
        })
    }

    deleteLayer(token, layerId) {
        return API.delete(DELETE_LAYER_ENDPOINT(token, layerId))
    }

    resetLayer(token, layerId) {
        return API.put(RESET_LAYER_ENDPOINT(token, layerId))
    }

    copyLayer(token, layerId) {
        return API.post(COPY_LAYER_ENDPOINT(token, layerId))
    }

    importConfig(token, file) {
        const formData = new FormData()
        formData.append('ut_config_file', file)

        return API.post(IMPORT_CONFIG_ENDPOINT(token), formData)
    }

    uploadTestFixtureFiles(token, fixtureFilesZip) {
        const formData = new FormData()
        formData.append('zip_file', fixtureFilesZip)

        return API.post(UPLOAD_TEST_FIXTURE_FILES_ENDPOINT(token), formData)
    }

    uploadReferenceSolution(token, file) {
        const formData = new FormData()
        formData.append('ref_solution_file', file)

        return API.post(UPLOAD_REFERENCE_SOLUTION_ENDPOINT(token), formData)
    }

    getReferenceSolution(token) {
        return API.get(GET_REFERENCE_SOLUTION_ENDPOINT(token))
    }

    getUnitTestResults(token) {
        return API.get(GET_UNIT_TEST_RESULTS_ENDPOINT(token))
    }

    generateAndProjectCriteria(token, layerId, description, submissionId, llmVersion = LLM_VERSIONS.FAST.value) {
        return API.post(GENERATE_AND_PROJECT_CRITERIA_ENDPOINT(token, layerId), {
            description,
            submission_id: submissionId,
            gpt_version: llmVersion,
        })
    }

    generateCriteria(token, feedback, llmVersion = LLM_VERSIONS.FAST.value) {
        return API.post(GENERATE_CRITERIA_ENDPOINT(token), {
            feedback,
            gpt_version: llmVersion,
        })
    }

    projectCriteria(token, criteria, submissionId, llmVersion = LLM_VERSIONS.FAST.value, layerId, patternId) {
        return API.post(PROJECT_CRITERIA_ENDPOINT(token), {
            criteria,
            submission_id: submissionId,
            gpt_version: llmVersion,
            layer_id: layerId,
            motif_id: patternId,
        })
    }

    createSemanticPattern(
        token,
        layerId,
        fileId,
        name,
        feedback,
        description,
        criteria,
        grade,
        colorId,
        groupingCap,
        regroup,
        hitFeedback,
        missFeedback,
        showHitReasoning,
        showMissReasoning,
        llmVersion = LLM_VERSIONS.FAST.value,
        warningLevel,
        conceptualLabels,
    ) {
        return API.post(CREATE_SEMANTIC_PATTERN_ENDPOINT(token), {
            pattern: {
                name,
                hit_feedback: hitFeedback,
                miss_feedback: missFeedback,
                show_hit_reasoning: showHitReasoning,
                show_miss_reasoning: showMissReasoning,
                description,
                criteria,
                grade,
                grouping_cap: groupingCap,
                color_id: colorId,
                gpt_version: llmVersion,
                warning_level: warningLevel,
                conceptual_labels: conceptualLabels,
            },
            layer_id: layerId,
            file_id: fileId,
            regroup,
        })
    }

    updatePatternCriteriaAndProject(token, layerId, patternId, criteria, regroup, oldGroupingCap) {
        return API.put(UPDATE_PATTERN_FROM_FEEDBACK_ENDPOINT(token), {
            layer_id: layerId,
            pattern_id: patternId,
            criteria,
            regroup,
            old_pattern2cap: isNil(oldGroupingCap) ? null : { [patternId]: oldGroupingCap },
        })
    }

    createSemanticPatterns(token, layerId, descriptions) {
        return API.post(CREATE_SEMANTIC_PATTERNS_ENDPOINT(token, layerId), {
            patterns: descriptions,
        })
    }

    runSemanticDiscovery(token, layerId, type, disciplines) {
        return API.post(SEMANTIC_DISCOVERY_ENDPOINT(token, layerId), {
            similarity_function: type,
            disciplines: disciplines,
        })
    }

    estimateProjectionCost(token, criteria, llmVersion = LLM_VERSIONS.FAST.value) {
        return API.post(ESTIMATE_PROJECTION_COST_ENDPOINT(token), {
            criteria,
            gpt_version: llmVersion,
        })
    }

    estimateDiscoveryCost(token, llmVersion = LLM_VERSIONS.FAST.value) {
        return API.post(ESTIMATE_DISCOVERY_COST_ENDPOINT(token), {
            gpt_version: llmVersion,
        })
    }

    regroup(token, layerId, oldPattern) {
        const payload = oldPattern ? { old_pattern2cap: oldPattern } : {}
        return API.post(REGROUP_ENDPOINT(token, layerId), payload)
    }

    subgroup(token, layerId, submissionId) {
        return API.post(SUBGROUP_ENDPOINT(token, layerId, submissionId))
    }
}

const AssignmentService = new _AssignmentService()
export default AssignmentService
